/**
 * page-listing.js
 * Custom Element <page-listing> — JS-driven facets, sort, pagination
 * for the Page Listing section (partners, resource library, etc.)
 *
 * Usage in Liquid:
 *   <page-listing data-section-id="{{ section.id }}" data-items-per-page="12">
 *     ...markup...
 *   </page-listing>
 */

/* ── Constants ──────────────────────────────────────────────── */
const FACETS = [
  { key: 'category',     label: 'Category' },
  { key: 'audience',     label: 'Audiences' },
  { key: 'service_area', label: 'Service Area' },
  { key: 'language',     label: 'Language' },
  { key: 'price_range',  label: 'Price Range', type: 'price' }
];

const SORT_OPTIONS = [
  { value: '',   label: 'Sort By' },
  { value: 'az', label: 'Alphabetically, A - Z' },
  { value: 'za', label: 'Alphabetically, Z - A' }
];

const SVG = {
  caret:      `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 9L12 15L18 9" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  chevRight:  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 8 14" fill="none" width="8" height="14"><path d="M1 1l6 6-6 6" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  chevLeft:   `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="12" viewBox="0 0 8 14" fill="none"><path d="M7 1L1 7l6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  chevRight2: `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="12" viewBox="0 0 8 14" fill="none"><path d="M1 1l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  arrowLeft:  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" width="16" height="16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>`,
  close:      `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6L6 18" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M6 6L18 18" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  closeSmall: `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`,
  globe:      `<svg class="plc-card__info-icon--lang" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`,
  pin:        `<svg class="plc-card__info-icon--area" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`,
  price:      `<svg class="plc-card__info-icon--price" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>`,
  external:   `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>`,
  heart:      `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>`
};

/* ── Utilities ───────────────────────────────────────────────── */
function toArray(val) {
  if (!val || val === '') return [];
  if (Array.isArray(val)) return val.map(v => String(v).trim());
  return String(val).split(',').map(v => v.trim()).filter(Boolean);
}

function escHtml(str) {
  if (!str) return '';
  const d = document.createElement('div');
  d.textContent = str;
  return d.innerHTML;
}

function formatPrice(n) {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/* ══════════════════════════════════════════════════════════════
   PageListingElement  — <page-listing> custom element
══════════════════════════════════════════════════════════════ */
class PageListingElement extends HTMLElement {

  /* ── Lifecycle ──────────────────────────────────────────── */
  connectedCallback() {
    this.sectionId    = this.dataset.sectionId;
    this.itemsPerPage = parseInt(this.dataset.itemsPerPage) || 12;

    // State
    this._allItems     = [];
    this._filteredItems= [];
    this._filters      = {};
    this._priceFilter  = { min: null, max: null };
    this._currentSort  = '';
    this._currentPage  = 1;
    this._facetValues  = {};
    this._expandedCards= {};

    this._loadData();
    this._extractFacetValues();
    this._renderDesktopFacets();
    this._render();
    this._buildMobileDrawer();
    this._bindEvents();
  }

  /* ── Data loading ───────────────────────────────────────── */
  _loadData() {
    const el = document.getElementById(`plg-data-${this.sectionId}`);
    if (!el) return;
    try {
      this._allItems = JSON.parse(el.textContent || '[]').map(item => ({
        id:            item.id,
        title:         item.title         || '',
        url:           item.url           || '',
        author:        item.author        || '',
        category:      toArray(item.category),
        audience:      toArray(item.audience),
        excerpt:       item.excerpt       || '',
        language:      toArray(item.language),
        service_area:  toArray(item.service_area),
        price_min:     parseFloat(item.price_min) || 0,
        price_max:     parseFloat(item.price_max) || 0,
        browse_website:item.browse_website || '',
        logo:          item.logo          || '',
        badges:        item.badges        || []
      }));
    } catch (e) {
      console.error('[page-listing] Failed to parse data:', e);
    }
  }

  _extractFacetValues() {
    this._facetValues = {};
    FACETS.filter(f => f.type !== 'price').forEach(f => {
      const counts = {};
      this._allItems.forEach(item => {
        (item[f.key] || []).forEach(v => { if (v) counts[v] = (counts[v] || 0) + 1; });
      });
      this._facetValues[f.key] = Object.keys(counts)
        .sort((a, b) => a.localeCompare(b))
        .map(k => ({ value: k, count: counts[k] }));
    });
  }

  /* ── Desktop facet dropdowns ─────────────────────────────── */
  _buildCheckboxHTML(facetKey, value, label, count, prefix = 'plgd') {
    const id      = `${prefix}-f-${facetKey}-${value.replace(/\W/g, '_')}`;
    const checked = this._isActive(facetKey, value);
    return `
      <li class="list-menu__item facets__item">
        <label for="${id}" class="facet-checkbox fcb-label">
          <input type="checkbox" id="${id}" data-facet="${facetKey}" data-value="${escHtml(value)}"${checked ? ' checked' : ''}>
          <span class="fcb" aria-hidden="true"></span>
          <span class="filter-label-text">${escHtml(label)} <span class="product-count">(${count})</span></span>
        </label>
      </li>`;
  }

  _renderDesktopFacets() {
    const container = this.querySelector('#plg-facets-desktop');
    if (!container) return;

    container.innerHTML = FACETS.map(facet => `
      <details class="plg-facet-details" data-facet-key="${facet.key}">
        <summary class="plg-facet-summary">${escHtml(facet.label)} ${SVG.caret}</summary>
        <div class="plg-facet-dropdown">
          <ul>
            ${facet.type === 'price'
              ? this._buildDesktopPriceHTML()
              : (this._facetValues[facet.key] || [])
                  .map(v => this._buildCheckboxHTML(facet.key, v.value, v.value, v.count))
                  .join('')
            }
          </ul>
        </div>
      </details>`
    ).join('');

    // One-at-a-time: close others when one opens
    container.querySelectorAll('.plg-facet-details').forEach(det => {
      det.addEventListener('toggle', () => {
        if (det.open) {
          container.querySelectorAll('.plg-facet-details').forEach(other => {
            if (other !== det) other.open = false;
          });
        }
      });
    });
  }

  _buildDesktopPriceHTML() {
    const min = this._priceFilter.min ?? '';
    const max = this._priceFilter.max ?? '';
    return `
      <li class="list-menu__item facets__item plg-price-row">
        <div class="plg-price-inputs">
          <div class="field">
            <input class="field__input" id="plgd-price-min" type="number" placeholder="0" min="0" value="${min}" data-price-min>
            <label class="field__label" for="plgd-price-min">From</label>
          </div>
          <span class="plg-price-sep">–</span>
          <div class="field">
            <input class="field__input" id="plgd-price-max" type="number" placeholder="Any" min="0" value="${max}" data-price-max>
            <label class="field__label" for="plgd-price-max">To</label>
          </div>
        </div>
      </li>`;
  }

  /* ── Mobile Drawer ───────────────────────────────────────── */
  _buildMobileDrawer() {
    const drawerEl = this.querySelector('#plg-drawer');
    if (!drawerEl) return;

    const panel = drawerEl.querySelector('.plg-drawer__panel');
    if (!panel) return;

    panel.innerHTML = '';

    /* 1. Close button row */
    const closeRow = this._createElement('div', 'plg-drawer__close-row',
      `<button type="button" class="plg-drawer__close-btn mobile-facets__close" id="plg-drawer-close">
        ${SVG.close}<span>Close</span>
      </button>`
    );
    panel.appendChild(closeRow);

    /* 2. "Filter and sort" heading */
    const heading = this._createElement('div', 'plg-drawer__heading-row', '<h2>Filter and sort</h2>');
    // panel.appendChild(heading);

    /* 3. Scrollable main area */
    const main = this._createElement('div', 'mobile-facets__main has-submenu gradient plg-drawer__main');
    main.appendChild(heading);
    panel.appendChild(main);

    /* 4. Filter rows (each with sliding submenu) */
    FACETS.forEach((facet, idx) => {
      const details = document.createElement('details');
      details.id        = `plg-mob-details-${idx}`;
      details.className = 'mobile-facets__details js-filter';
      details.dataset.facetKey = facet.key;

      details.innerHTML = `
        <summary class="mobile-facets__summary focus-inset">
          <div>
            <span>${escHtml(facet.label)}</span>
            <span class="mobile-facets__arrow no-js-hidden">${SVG.chevRight}</span>
          </div>
        </summary>
        <div class="mobile-facets__submenu gradient" id="plg-mob-submenu-${idx}">
          <button type="button" class="mobile-facets__close-button link link--text focus-inset"
            aria-expanded="true" data-plg-back>
            ${SVG.arrowLeft} ${escHtml(facet.label)}
          </button>
          <ul class="mobile-facets__list list-unstyled">
            ${facet.type === 'price'
              ? this._buildMobilePriceHTML()
              : (this._facetValues[facet.key] || [])
                  .map(v => this._buildMobileCheckboxItem(facet.key, v.value, v.value, v.count, idx))
                  .join('')
            }
          </ul>
          <div class="no-js-hidden mobile-facets__footer gradient">
            <button type="button" class="mobile-facets__clear-wrapper button" data-plg-sub-clear="${facet.key}">Clear</button>
            <button type="button" class="button button--primary" data-plg-back>Done</button>
          </div>
        </div>`;

      details.addEventListener('toggle', () => {
        if (details.open) {
          requestAnimationFrame(() => details.classList.add('menu-opening'));
        } else {
          details.classList.remove('menu-opening');
        }
      });

      main.appendChild(details);
    });

    /* 5. Sort row */
    const sortOptions = SORT_OPTIONS.map(o =>
      `<option value="${o.value}"${this._currentSort === o.value ? ' selected' : ''}>${escHtml(o.label)}</option>`
    ).join('');

    main.appendChild(this._createElement('div', 'mobile-facets__details js-filter', `
      <div class="mobile-facets__summary">
        <div class="mobile-facets__sort">
          <label for="plg-sort-mobile">Sort by:</label>
          <div class="select">
            <select id="plg-sort-mobile" class="select__select" data-plg-sort-select>
              ${sortOptions}
            </select>
            ${SVG.caret}
          </div>
        </div>
      </div>`
    ));

    /* 6. Footer */
    main.appendChild(this._createElement('div', 'mobile-facets__footer plg-drawer__footer', `
      <button type="button" class="mobile-facets__clear-wrapper button" id="plg-drawer-clear">Remove All</button>
      <button type="button" class="button button--primary" id="plg-drawer-apply">Apply</button>`
    ));

    /* Overlay click */
    drawerEl.querySelector('.plg-drawer__overlay')
      ?.addEventListener('click', () => this._closeDrawer());
  }

  _buildMobilePriceHTML() {
    const min = this._priceFilter.min ?? '';
    const max = this._priceFilter.max ?? '';
    return `
      <li class="mobile-facets__item list-menu__item plg-mobile-price">
        <div class="facets__price plg-price-inputs-mobile">
          <span class="field-currency">$</span>
          <div class="field">
            <input class="field__input" id="plg-mob-price-min" type="number"
              placeholder="0" min="0" inputmode="decimal" value="${min}" data-mob-price-min>
            <label class="field__label" for="plg-mob-price-min">From</label>
          </div>
          <span class="field-currency">$</span>
          <div class="field">
            <input class="field__input" id="plg-mob-price-max" type="number"
              placeholder="Any" min="0" inputmode="decimal" value="${max}" data-mob-price-max>
            <label class="field__label" for="plg-mob-price-max">To</label>
          </div>
        </div>
      </li>`;
  }

  _buildMobileCheckboxItem(facetKey, value, label, count, groupIdx) {
    const id      = `plg-mob-${groupIdx}-${value.replace(/\W/g, '_')}`;
    const checked = this._isActive(facetKey, value);
    const disabled= count === 0 && !checked;
    return `
      <li class="mobile-facets__item list-menu__item">
        <label for="${id}" class="mobile-facets__label fcb-label${disabled ? ' mobile-facets__label--disabled' : ''}">
          <input class="mobile-facets__checkbox" type="checkbox" id="${id}"
            data-facet="${facetKey}" data-value="${escHtml(value)}"${checked ? ' checked' : ''}${disabled ? ' disabled' : ''}>
          <span class="mobile-facets__highlight"></span>
          <span class="fcb" aria-hidden="true"></span>
          <span aria-hidden="true">${escHtml(label)} <span class="product-count">(${count})</span></span>
        </label>
      </li>`;
  }

  /* ── Filter state ────────────────────────────────────────── */
  _isActive(key, value) {
    return !!(this._filters[key]?.includes(value));
  }

  _toggleFilter(key, value, checked) {
    if (!this._filters[key]) this._filters[key] = [];
    if (checked) {
      if (!this._filters[key].includes(value)) this._filters[key].push(value);
    } else {
      this._filters[key] = this._filters[key].filter(v => v !== value);
      if (this._filters[key].length === 0) delete this._filters[key];
    }
    this._currentPage = 1;
    this._render();
    this._syncCheckboxes();
  }

  _clearGroupFilter(key) {
    delete this._filters[key];
    if (key === 'price_range') this._priceFilter = { min: null, max: null };
    this._currentPage = 1;
    this._render();
    this._syncCheckboxes();
    this._syncPriceInputs();
  }

  _clearAllFilters() {
    this._filters      = {};
    this._priceFilter  = { min: null, max: null };
    this._currentPage  = 1;
    this._render();
    this._syncCheckboxes();
    this._syncPriceInputs();
  }

  _removeFilter(key, value) {
    this._toggleFilter(key, value, false);
  }

  _applyPriceFilter(min, max) {
    this._priceFilter.min = min !== '' && min != null ? parseFloat(min) : null;
    this._priceFilter.max = max !== '' && max != null ? parseFloat(max) : null;
    this._currentPage = 1;
    this._render();
  }

  _getFilteredItems() {
    let items = this._allItems.slice();

    // Free-input price filter
    if (this._priceFilter.min != null || this._priceFilter.max != null) {
      items = items.filter(item => {
        if (this._priceFilter.min != null && item.price_min < this._priceFilter.min) return false;
        if (this._priceFilter.max != null && item.price_min > this._priceFilter.max) return false;
        return true;
      });
    }

    // Facet filters (OR within facet, AND across facets)
    const keys = Object.keys(this._filters);
    return items.filter(item =>
      keys.every(key => {
        const selected = this._filters[key];
        if (!selected?.length) return true;
        return selected.some(sv => (item[key] || []).includes(sv));
      })
    );
  }

  /* ── Sort ───────────────────────────────────────────────── */
  _sortItems(items) {
    if (!this._currentSort) return items;
    const sorted = [...items].sort((a, b) => a.title.localeCompare(b.title));
    return this._currentSort === 'za' ? sorted.reverse() : sorted;
  }

  _setSort(sortKey) {
    this._currentSort = sortKey;
    this._currentPage = 1;
    this._render();
    this._updateSortUI();
  }

  _updateSortUI() {
    this.querySelectorAll('.plg-sort-btn').forEach(btn => {
      btn.classList.toggle('plg-sort-btn--active', btn.dataset.sort === this._currentSort);
    });
    this.querySelector('.plg-sort-details')?.removeAttribute('open');
  }

  /* ── Sync helpers ────────────────────────────────────────── */
  _syncCheckboxes() {
    document.querySelectorAll('input[data-facet]').forEach(cb => {
      cb.checked = this._isActive(cb.dataset.facet, cb.dataset.value);
    });
  }

  _syncPriceInputs() {
    ['[data-price-min]', '[data-mob-price-min]'].forEach(sel => {
      const el = document.querySelector(sel);
      if (el) el.value = '';
    });
    ['[data-price-max]', '[data-mob-price-max]'].forEach(sel => {
      const el = document.querySelector(sel);
      if (el) el.value = '';
    });
  }

  /* ── Main render ──────────────────────────────────────────── */
  _render() {
    this._filteredItems = this._sortItems(this._getFilteredItems());

    const total     = this._filteredItems.length;
    const pageItems = this._filteredItems.slice(
      (this._currentPage - 1) * this.itemsPerPage,
      this._currentPage * this.itemsPerPage
    );

    this._renderCount(total);
    this._renderCards(pageItems);
    this._renderPagination(total);
    this._renderActiveFilters();
  }

  _renderCount(total) {
    const label = `${total} Result${total !== 1 ? 's' : ''}`;
    const el    = this.querySelector('#plg-result-count');
    const mob   = this.querySelector('#plg-mobile-count');
    if (el)  el.textContent  = label;
    if (mob) mob.textContent = label;
  }

  /* ── Card rendering ──────────────────────────────────────── */
  _renderCards(items) {
    const container = this.querySelector('#plg-cards');
    if (!container) return;

    if (!items.length) {
      container.innerHTML = `<div class="plg-empty"><p>No results found. Try adjusting your filters.</p></div>`;
      return;
    }
    container.innerHTML = items.map(item => this._cardHTML(item)).join('');
  }

  _cardHTML(item) {
    const expanded = !!this._expandedCards[item.id];
    const category = item.category.join(', ');
    const audience = item.audience.join(', ');

    return `
      <div class="plc-card" data-card-id="${item.id}">
        <div class="plc-card__logo">
          ${item.logo
            ? `<img src="${escHtml(item.logo)}" alt="" class="plc-card__logo-img" loading="lazy">`
            : `<div class="plc-card__logo-placeholder"></div>`}
        </div>
        <div class="plc-card__content">
          <div class="plc-card__header">
            <div>
              <a href="${escHtml(item.browse_website)}" class="plc-card__title">${escHtml(item.title)}</a>
              ${item.author ? `<p class="plc-card__author">by ${escHtml(item.author)}</p>` : ''}
            </div>
            <button type="button" class="plc-card__wishlist" aria-label="Add to wishlist">${SVG.heart}</button>
          </div>

          ${category || audience ? `
            <div class="plc-card__attrs">
              ${category ? `<div class="plc-card__attr"><span class="plc-card__attr-label">Category</span><span class="plc-card__attr-value">${escHtml(category)}</span></div>` : ''}
              ${audience ? `<div class="plc-card__attr"><span class="plc-card__attr-label">Audience</span><span class="plc-card__attr-value">${escHtml(audience)}</span></div>` : ''}
            </div>` : ''}

          ${item.excerpt ? `
            <p class="plc-card__excerpt${expanded ? '' : ' plc-card__excerpt--collapsed'}">
              ${escHtml(item.excerpt)}
            </p>` : ''}

          ${item.language.length || item.service_area.length || item.price_min > 0 ? `
            <div class="plc-card__info-row">
              ${item.language.length     ? `<span class="plc-card__info-item">${SVG.globe} ${escHtml(item.language.join(', '))}</span>` : ''}
              ${item.service_area.length ? `<span class="plc-card__info-item">${SVG.pin} ${escHtml(item.service_area.join(', '))}</span>` : ''}
              ${item.price_min > 0       ? `<span class="plc-card__info-item">${SVG.price} Min. USD ${formatPrice(item.price_min)}</span>` : ''}
            </div>` : ''}

          <div class="plc-card__footer">
            ${item.browse_website
              ? `<a href="${escHtml(item.browse_website)}" class="plc-card__browse" target="_blank" rel="noopener noreferrer">${SVG.external} Browse Website</a>`
              : `<span></span>`}
            ${item.excerpt?.length > 150 ? `
              <button type="button" class="plc-card__toggle${expanded ? ' plc-card__toggle--expanded' : ''}" data-toggle-id="${item.id}">
                <span>${expanded ? 'Show Less' : 'Show More'}</span> ${SVG.caret}
              </button>` : ''}
          </div>
        </div>
      </div>`;
  }

  /* ── Pagination ──────────────────────────────────────────── */
  _renderPagination(total) {
    const container  = this.querySelector('#plg-pagination');
    if (!container) return;

    const totalPages = Math.ceil(total / this.itemsPerPage);
    if (totalPages <= 1) { container.innerHTML = ''; return; }

    const current = this._currentPage;
    const pages   = this._pageNumbers(current, totalPages);

    container.innerHTML = `
      <div class="pagination">
        <button type="button" class="pagination__item${current === 1 ? ' pagination__item--disabled' : ''}" data-page="${current - 1}">${SVG.chevLeft}</button>
        ${pages.map(p => p === '...'
          ? `<span class="pagination__ellipsis">…</span>`
          : `<button type="button" class="pagination__item${p === current ? ' pagination__item--current' : ''}" data-page="${p}">${p}</button>`
        ).join('')}
        <button type="button" class="pagination__item${current === totalPages ? ' pagination__item--disabled' : ''}" data-page="${current + 1}">${SVG.chevRight2}</button>
      </div>`;
  }

  _pageNumbers(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = [1];
    if (current > 3) pages.push('...');
    const start = Math.max(2, current - 1);
    const end   = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (current < total - 2) pages.push('...');
    pages.push(total);
    return pages;
  }

  _goToPage(page) {
    const totalPages = Math.ceil(this._filteredItems.length / this.itemsPerPage);
    if (page < 1 || page > totalPages) return;
    this._currentPage = page;
    this._render();
    const top = window.scrollY + this.getBoundingClientRect().top - 120;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  /* ── Active filter pills ─────────────────────────────────── */
  _renderActiveFilters() {
    const container = this.querySelector('#plg-active-filters');
    if (!container) return;

    const keys     = Object.keys(this._filters);
    const hasPrice = this._priceFilter.min != null || this._priceFilter.max != null;

    if (!keys.length && !hasPrice) { container.innerHTML = ''; return; }

    const pills = [];

    keys.forEach(key => {
      const label = FACETS.find(f => f.key === key)?.label ?? key;
      this._filters[key].forEach(value => {
        pills.push(`
          <button type="button" class="plg-active-pill" data-remove-facet="${key}" data-remove-value="${escHtml(value)}">
            ${escHtml(label)}: ${escHtml(value)} ${SVG.closeSmall}
          </button>`);
      });
    });

    if (hasPrice) {
      const parts = [];
      if (this._priceFilter.min != null) parts.push(`From ${this._priceFilter.min}`);
      if (this._priceFilter.max != null) parts.push(`To ${this._priceFilter.max}`);
      pills.push(`
        <button type="button" class="plg-active-pill" data-remove-price>
          Price Range: ${parts.join(' ')} ${SVG.closeSmall}
        </button>`);
    }

    pills.push(`<button type="button" class="plg-active-clear" id="plg-clear-all">Clear all</button>`);
    container.innerHTML = pills.join('');
  }

  /* ── Show More/Less ──────────────────────────────────────── */
  _toggleExpand(cardId) {
    this._expandedCards[cardId] = !this._expandedCards[cardId];
    const cardEl = this.querySelector(`[data-card-id="${cardId}"]`);
    if (!cardEl) return;

    const isExpanded = this._expandedCards[cardId];
    
    // Toggle excerpt class
    const excerptEl = cardEl.querySelector('.plc-card__excerpt');
    if (excerptEl) {
      if (isExpanded) {
        excerptEl.classList.remove('plc-card__excerpt--collapsed');
      } else {
        excerptEl.classList.add('plc-card__excerpt--collapsed');
      }
    }

    // Toggle button state
    const toggleBtn = cardEl.querySelector('.plc-card__toggle');
    if (toggleBtn) {
      if (isExpanded) {
        toggleBtn.classList.add('plc-card__toggle--expanded');
      } else {
        toggleBtn.classList.remove('plc-card__toggle--expanded');
      }
      const span = toggleBtn.querySelector('span');
      if (span) {
        span.textContent = isExpanded ? 'Show Less' : 'Show More';
      }
    }
  }

  /* ── Drawer open/close ───────────────────────────────────── */
  _openDrawer() {
    this.querySelector('#plg-drawer')?.classList.add('plg-drawer--open');
    document.body.style.overflow = 'hidden';
  }

  _closeDrawer() {
    const drawer = this.querySelector('#plg-drawer');
    if (!drawer) return;
    // Close any open submenu first (animate back)
    drawer.querySelectorAll('.mobile-facets__details.menu-opening').forEach(d => {
      d.classList.remove('menu-opening');
      setTimeout(() => { d.open = false; }, 400);
    });
    drawer.classList.remove('plg-drawer--open');
    document.body.style.overflow = '';
  }

  /* ── Event binding ───────────────────────────────────────── */
  _bindEvents() {
    // Checkbox changes — both desktop and mobile
    document.addEventListener('change', e => {
      const cb = e.target.closest('input[data-facet]');
      if (!cb || !this.contains(cb) && !this.querySelector('#plg-drawer')?.contains(cb)) return;
      this._toggleFilter(cb.dataset.facet, cb.dataset.value, cb.checked);
      // Twin sync
      document.querySelectorAll(
        `input[data-facet="${cb.dataset.facet}"][data-value="${cb.dataset.value}"]`
      ).forEach(twin => { if (twin !== cb) twin.checked = cb.checked; });
    });

    // Sort select (mobile drawer)
    document.addEventListener('change', e => {
      if (!e.target.matches('[data-plg-sort-select]')) return;
      this._setSort(e.target.value);
    });

    // All clicks
    document.addEventListener('click', e => {
      // Desktop sort pill
      const sortBtn = e.target.closest('.plg-sort-btn');
      if (sortBtn && this.contains(sortBtn)) {
        this._setSort(sortBtn.dataset.sort);
        return;
      }

      // Desktop price apply
      if (e.target.closest('[data-price-apply]') && this.contains(e.target)) {
        this._applyPriceFilter(
          this.querySelector('[data-price-min]')?.value,
          this.querySelector('[data-price-max]')?.value
        );
        return;
      }

      // Pagination
      const pageBtn = e.target.closest('[data-page]');
      if (pageBtn?.closest('#plg-pagination') && this.contains(pageBtn)) {
        this._goToPage(parseInt(pageBtn.dataset.page));
        return;
      }

      // Show more/less card
      const toggleBtn = e.target.closest('[data-toggle-id]');
      if (toggleBtn && this.contains(toggleBtn)) {
        this._toggleExpand(toggleBtn.dataset.toggleId);
        return;
      }

      // Remove active filter pill
      const pill = e.target.closest('[data-remove-facet]');
      if (pill && this.contains(pill)) {
        this._removeFilter(pill.dataset.removeFacet, pill.dataset.removeValue);
        return;
      }

      // Remove price pill
      if (e.target.closest('[data-remove-price]') && this.contains(e.target)) {
        this._priceFilter = { min: null, max: null };
        this._currentPage = 1;
        this._render();
        this._syncPriceInputs();
        return;
      }

      // Clear all
      if (e.target.closest('#plg-clear-all') && this.contains(e.target)) {
        this._clearAllFilters();
        return;
      }

      // Open mobile drawer
      if (e.target.closest('#plg-mobile-filter-btn') && this.contains(e.target)) {
        this._openDrawer();
        return;
      }

      // Close drawer
      if (e.target.closest('#plg-drawer-close') && this.contains(e.target)) {
        this._closeDrawer();
        return;
      }

      // Drawer: Remove All
      if (e.target.closest('#plg-drawer-clear') && this.contains(e.target)) {
        this._clearAllFilters();
        return;
      }

      // Drawer: Apply
      if (e.target.closest('#plg-drawer-apply') && this.contains(e.target)) {
        this._closeDrawer();
        return;
      }

      // Submenu back button
      if (e.target.closest('[data-plg-back]') && this.contains(e.target)) {
        const open = this.querySelector('.mobile-facets__details.menu-opening');
        if (open) {
          open.classList.remove('menu-opening');
          setTimeout(() => { open.open = false; }, 400);
        }
        return;
      }

      // Submenu: Clear single facet
      const subClear = e.target.closest('[data-plg-sub-clear]');
      if (subClear && this.contains(subClear)) {
        this._clearGroupFilter(subClear.dataset.plgSubClear);
        return;
      }

      // Mobile price apply (on input Enter)
      if (e.target.closest('[data-mob-price-apply]') && this.contains(e.target)) {
        this._applyPriceFilter(
          this.querySelector('[data-mob-price-min]')?.value,
          this.querySelector('[data-mob-price-max]')?.value
        );
        return;
      }
    });

    // Enter key on price inputs
    this.addEventListener('keydown', e => {
      if (e.key !== 'Enter') return;
      if (e.target.matches('[data-price-min],[data-price-max]')) {
        this._applyPriceFilter(
          this.querySelector('[data-price-min]')?.value,
          this.querySelector('[data-price-max]')?.value
        );
      }
      if (e.target.matches('[data-mob-price-min],[data-mob-price-max]')) {
        this._applyPriceFilter(
          this.querySelector('[data-mob-price-min]')?.value,
          this.querySelector('[data-mob-price-max]')?.value
        );
      }
    });

    // Close desktop dropdowns on outside click
    document.addEventListener('click', e => {
      if (e.target.closest('.plg-facet-details') || e.target.closest('.plg-sort-details')) return;
      this.querySelectorAll('.plg-facet-details[open], .plg-sort-details[open]').forEach(d => {
        d.open = false;
      });
    });
  }

  /* ── DOM helper ──────────────────────────────────────────── */
  _createElement(tag, className, innerHTML = '') {
    const el = document.createElement(tag);
    if (className) el.className = className;
    el.innerHTML = innerHTML;
    return el;
  }
}

customElements.define('page-listing', PageListingElement);
